/* Общий хвост заказа (APP-SPEC-4 §8, BotRun прототипа): модель поверх ответа GET /app/orders/{id}.
   Всё, что рисует хвост, берётся из заказа сервера (03-api.md §9.4): этапы steps[], step_index, progress,
   error {kind, step_key}, charge и balance. Чего сервер не прислал, хвост не выдумывает: нет этапов — нет дуг
   и списка, нет суммы — нет чека. */
import type { SpeechOrder } from '../../../api/speech';
import { uiFormat, uiText } from '../../../i18n';

export type TailPhase = 'run' | 'done' | 'mod' | 'tech' | 'uncertain';
export interface TailStep {
  key: string;
  icon: string;
  title: string;
  titleDone: string;
  status: 'pending' | 'running' | 'done' | 'failed' | 'skipped';
  expectedMs: number | null;
}
export interface TailProgress {
  done: number;
  total: number;
  note: string;
}
/** Необязательные поля заказа по контракту §9.4; в SpeechOrder их нет, поэтому читаются с проверкой. */
interface OrderExtras {
  steps?: unknown;
  step_index?: unknown;
  progress?: unknown;
  error?: unknown;
  charge?: unknown;
  balance?: unknown;
  deliver?: unknown;
  display?: unknown;
}
const moderationCodes = new Set([
  'moderation_rejected',
  'circle_moderation_rejected',
  'rights_rejected',
  'photos_rejected',
  'text_moderation_rejected',
  'content_rejected',
]);
const statuses = new Set(['pending', 'running', 'done', 'failed', 'skipped']);
const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
const extras = (order: SpeechOrder | null | undefined) => (order ?? {}) as SpeechOrder & OrderExtras;

/** Ошибка итога: kind из error, иначе — по коду (моderation-коды §9.5). */
export function tailError(order: SpeechOrder | null | undefined): { kind: 'moderation' | 'tech'; code: string; stepKey: string; message: string } | null {
  if (!order || order.status !== 'failed') return null;
  const error = record(extras(order).error);
  const code = typeof error.code === 'string' ? error.code : (order.error_code ?? '');
  const kind = error.kind === 'moderation' || error.kind === 'tech' ? error.kind : moderationCodes.has(code) ? 'moderation' : 'tech';
  return {
    kind,
    code,
    stepKey: typeof error.step_key === 'string' ? error.step_key : '',
    message: typeof error.message === 'string' ? error.message : '',
  };
}
export function tailPhase(order: SpeechOrder | null | undefined): TailPhase {
  if (!order) return 'run';
  if (order.status === 'done') return 'done';
  if (order.status === 'uncertain') return 'uncertain';
  if (order.status === 'failed') return tailError(order)?.kind === 'moderation' ? 'mod' : 'tech';
  return 'run';
}
/** Этапы заказа: только валидные записи сервера. */
export function tailSteps(order: SpeechOrder | null | undefined): TailStep[] {
  const raw = extras(order).steps;
  if (!Array.isArray(raw) || raw.length > 12) return [];
  const steps: TailStep[] = [];
  for (const item of raw) {
    const step = record(item);
    if (typeof step.key !== 'string' || typeof step.title !== 'string' || !step.title.trim()) return [];
    steps.push({
      key: step.key,
      icon: typeof step.icon === 'string' ? step.icon : 'sparkle',
      title: step.title,
      titleDone: typeof step.title_done === 'string' && step.title_done.trim() ? step.title_done : step.title,
      status: statuses.has(String(step.status)) ? (step.status as TailStep['status']) : 'pending',
      expectedMs: Number.isFinite(step.expected_ms) && Number(step.expected_ms) > 0 ? Number(step.expected_ms) : null,
    });
  }
  return steps;
}
/** Текущий этап: step_index сервера, иначе первый не пройденный; у ошибки — этап step_key. */
export function tailCurrent(order: SpeechOrder | null | undefined, steps: TailStep[]): number {
  if (!steps.length) return 0;
  const error = tailError(order);
  if (error?.stepKey) {
    const index = steps.findIndex(step => step.key === error.stepKey);
    if (index >= 0) return index;
  }
  const failed = steps.findIndex(step => step.status === 'failed');
  if (failed >= 0) return failed;
  const index = Number(extras(order).step_index);
  if (Number.isSafeInteger(index) && index >= 0) return Math.min(index, steps.length - 1);
  const open = steps.findIndex(step => !['done', 'skipped'].includes(step.status));
  return open >= 0 ? open : steps.length - 1;
}
export function tailProgress(order: SpeechOrder | null | undefined): TailProgress | null {
  const value = record(extras(order).progress);
  const done = Number(value.done),
    total = Number(value.total);
  if (!Number.isSafeInteger(done) || !Number.isSafeInteger(total) || total <= 0 || done < 0 || done > total) return null;
  return { done, total, note: typeof value.note === 'string' ? value.note : '' };
}
/** Чек: сумма из charge (§9.4) или из финансового состояния заказа; остаток — только если сервер его прислал. */
export function tailReceipt(order: SpeechOrder | null | undefined): { kind: 'debit' | 'refund' | 'free'; amount: number | null; balance: number | null } | null {
  if (!order) return null;
  const charge = record(extras(order).charge);
  const balanceRaw = record(extras(order).balance).circle;
  const balance = Number.isFinite(balanceRaw) ? Number(balanceRaw) : null;
  const financial = order.financial;
  if (financial?.state === 'dev_free') return { kind: 'free', amount: null, balance };
  const refunded = Number(charge.refunded);
  if ((Number.isFinite(refunded) && refunded > 0) || financial?.state === 'refunded') {
    const amount = Number.isFinite(refunded) && refunded > 0 ? refunded : Number(financial?.amount_tokens);
    return { kind: 'refund', amount: Number.isFinite(amount) ? amount : null, balance };
  }
  const debited = Number(charge.debited);
  const amount = Number.isFinite(debited) && debited > 0 ? debited : Number(financial?.amount_tokens);
  if (financial?.state === 'debited' || (Number.isFinite(debited) && debited > 0))
    return { kind: 'debit', amount: Number.isFinite(amount) ? amount : null, balance };
  return null;
}
/** «on_request» — бот присылает результат только по кнопке итога (§12.4: «Сменить голос», «Почистить запись»). */
export function tailDeliver(order: SpeechOrder | null | undefined): 'auto' | 'on_request' | null {
  const value = extras(order).deliver;
  return value === 'auto' || value === 'on_request' ? value : null;
}
export function tailDisplay(order: SpeechOrder | null | undefined): Record<string, unknown> {
  return record(extras(order).display);
}
export const cNum = (value: number) => Math.round(value).toLocaleString('ru-RU');
export function mmss(seconds: number) {
  const s = Math.max(0, Math.round(seconds || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
/** Подпись под заголовком ошибки — как textHTML() BotRun, но суммы только из заказа. */
export function tailFailText(
  phase: 'mod' | 'tech',
  order: SpeechOrder | null | undefined,
  texts: { modWhy?: string; techWhy?: string; notMade: string },
  step: TailStep | undefined,
) {
  const receipt = tailReceipt(order);
  const amount = receipt?.amount ?? null;
  if (phase === 'mod') {
    const why = uiText(texts.modWhy ?? 'Политику и запрещённые темы не пропускаем.');
    return amount !== null ? `${why} ${uiFormat('{0} C списаны, {1}.', cNum(amount), uiText(texts.notMade))}` : `${why} ${capital(uiText(texts.notMade))}.`;
  }
  const refund = amount !== null ? ' ' + uiFormat('{0} C вернулись на баланс.', cNum(amount)) : '';
  // Отказ по входу, а не сбой сервиса (например, stt_no_speech): экран называет причину сам.
  if (texts.techWhy) return `${uiText(texts.techWhy)}${refund}`;
  const where = step ? uiFormat(', на шаге «{0}»', uiText(step.title).toLowerCase()) : '';
  return `${uiText('Сбой на нашей стороне')}${where}.${amount !== null ? ' ' + uiFormat('{0} C вернулись на баланс.', cNum(amount)) : ''}`;
}
const capital = (value: string) => (value ? value[0].toUpperCase() + value.slice(1) : value);
